import React, { useEffect } from 'react';
import { Drawer, Form, Button, SelectPicker, Message, useToaster } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeaturesLeave } from '@/services/feature.service';
import { useTranslation } from 'react-i18next';

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    operationOwnerId: string;
    type: string;
    transaction: string;
  };
  users: any[];
  operationOwners: any[];
  usersLoading: boolean;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  operationOwners,
  usersLoading
}) => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const [formValue, setFormValue] = React.useState(currentFilters);
  const { leaveTypes = [], loading: leaveTypesLoading = false } = useSelector(
    (state: any) => state.feature || {}
);
const { t } = useTranslation();

  const transactionOptions = [
    { label: t('All'), value: '' },
    { label: t('Admin Update'), value: 'ADMIN_UPDATE' },
    { label: t('Leave Submission'), value: 'LEAVE_SUBMISSION' },
    { label: t('Leave Approval'), value: 'LEAVE_APPROVAL' }
  ];

  const leaveTypeOptions = React.useMemo(() => {
    if (!leaveTypes?.length) return [{ label: t('All'), value: '' }];
    
    return [
      { label: t('All'), value: '' },
      ...leaveTypes.map((leaveType: any) => ({
        label: leaveType.type,
        value: leaveType.type
      }))
    ];
  }, [leaveTypes]);

  const userOptions = React.useMemo(() => {
    const uniqueUsers = Array.from(new Set(users.map(user => user._id)))
      .map(id => users.find(user => user._id === id))
      .filter(Boolean);

    return uniqueUsers.map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id
    }));
  }, [users]);

  const operationOwnerOptions = React.useMemo(() => {
    const uniqueOwners = Array.from(new Set(operationOwners.map(owner => owner._id)))
      .map(id => operationOwners.find(owner => owner._id === id))
      .filter(Boolean);

    return uniqueOwners.map(owner => ({
      label: `${owner.firstname} ${owner.lastname}`,
      value: owner._id
    }));
  }, [operationOwners]);

  useEffect(() => {
    if (open) {
      dispatch(fetchFeaturesLeave());
      setFormValue(currentFilters);
    }
  }, [open, currentFilters, dispatch]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);
    
    if (hasFilters) {
      onFiltersChange(formValue);
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      operationOwnerId: '',
      type: '',
      transaction: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  return (
    <Drawer 
      backdrop="static" 
      size="sm" 
      placement="right" 
      onClose={onClose} 
      open={open}
    >
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control 
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t("Select user")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Operation Owner')}</Form.ControlLabel>
            <Form.Control 
              name="operationOwnerId"
              accepter={SelectPicker}
              data={operationOwnerOptions}
              loading={usersLoading}
              searchable
              placeholder={t("Select operation owner")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Leave Type')}</Form.ControlLabel>
            <Form.Control 
              name="type"
              accepter={SelectPicker}
              data={leaveTypeOptions}
              loading={leaveTypesLoading}
              searchable
              placeholder={t("Select leave type")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Transaction Type')}</Form.ControlLabel>
            <Form.Control 
              name="transaction"
              accepter={SelectPicker}
              data={transactionOptions}
              placeholder={t("Select transaction type")}
              block
              placement='auto'
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;